import {Component, MeshComponent, Object3D} from '@wonderlandengine/api';
import {vec3} from 'gl-matrix';
import {hapticFeedback} from '../utils/haptic-feedback.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {Cursor, CursorTarget} from '@wonderlandengine/components';
import {PhongMaterial} from '../types/materials.js';
import {CursorReticle, CursorType} from './cursor-reticle.js';

import {SoundManagerInstance, Sounds} from '../managers/soundmanager.js';

/**
 * drawer-interaction
 */
export class DrawerInteraction extends Component {
    static TypeName = 'drawer-interaction';

    /* Properties that are configurable in the editor */

    @property.object()
    player?: Object3D;

    @property.float(1)
    speedSeconds = 1;

    @property.float(0)
    xOffset = 0;

    @property.float(0)
    yOffset = 0;

    @property.float(0)
    zOffset = 0;

    private lerpTime = 100;
    private opening = true;
    private startPos: vec3 = new Float32Array(3);
    private currentPos: vec3 = new Float32Array(3);
    private lerpRatio = 0;
    private lerpResult: vec3 = new Float32Array(3);

    start() {
        /* Add Cursor Functions */
        const target =
            this.object.getComponent(CursorTarget) ||
            this.object.addComponent(CursorTarget)!;
        target.onHover.add(this.onHover.bind(this));
        target.onUnhover.add(this.onUnHover.bind(this));
        target.onDown.add(this.onDown.bind(this));

        /* Clone objectHover material, to only affect this object's material */
        const parent = this.object.parent;
        if (parent) {
            parent.getComponents(MeshComponent).forEach((a) => {
                if (a.material) {
                    a.material = a.material.clone();
                }
            });

            /* Lerp Open Drawer */
            this.lerpTime = 100;
            this.opening = true; //Gets toggled

            /* Store Location */
            this.startPos = new Float32Array(3);
            this.currentPos = new Float32Array(3);
            parent.getPositionWorld(this.startPos);
        } else {
            console.warn('DrawerInteraction component requires the parent to be set.');
        }
    }

    update(dt: number) {
        /* Lerp Open Drawer */
        this.lerpTime += dt;
        this.lerpRatio = this.lerpTime / this.speedSeconds; /* Seconds */
        if (this.lerpTime < this.speedSeconds) {
            if (this.opening) {
                /* Close Drawer */
                this.lerpResult = vec3.lerp(
                    vec3.create(),
                    this.currentPos,
                    this.startPos,
                    this.lerpRatio
                );
            } else {
                /* Open Drawer, based on Offset */
                this.lerpResult = vec3.lerp(
                    vec3.create(),
                    this.currentPos,
                    [
                        this.startPos[0] + this.xOffset,
                        this.startPos[1] + this.yOffset,
                        this.startPos[2] + this.zOffset,
                    ],
                    this.lerpRatio
                );
            }
            if (this.object.parent) {
                this.object.parent.setPositionWorld(this.lerpResult);
            }
        }
    }

    onHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.5, 50);
        /* Highlight */
        if (this.object.parent) {
            this.object.parent.getComponents(MeshComponent).forEach((a) => {
                const material = a.material as PhongMaterial;
                if (material) {
                    material.emissiveColor = [0.12, 0.12, 0.12, 1];
                }
            });
        }

        if (this.player) {
            const cursorReticle = this.player.getComponent(CursorReticle);
            if (cursorReticle) {
                cursorReticle.changeCursorTexture(cursor, CursorType.Pointer);
            }
        } else {
            console.warn('DrawerInteraction component requires the player to be set.');
        }
    }

    onUnHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.3, 50);
        /* Highlight Reset to Default */
        if (this.object.parent) {
            this.object.parent.getComponents(MeshComponent).forEach((a) => {
                const material = a.material as PhongMaterial;
                if (material) {
                    material.emissiveColor = [0, 0, 0, 1];
                }
            });
        }

        /* Cursor */
        if (this.player) {
            const cursorReticle = this.player.getComponent(CursorReticle);
            if (cursorReticle) {
                cursorReticle.changeCursorTexture(cursor, CursorType.Default);
            }
        }
    }

    onDown(_: Object3D, cursor: Cursor) {
        SoundManagerInstance.playSound(
            this.opening ? Sounds.drawerOpen : Sounds.drawerClose
        );

        this.opening = !this.opening; /* toggle */
        if (this.object.parent) {
            this.object.parent.getPositionWorld(this.currentPos);
        }
        this.lerpTime = 0; /* Start Lerp */
        hapticFeedback(cursor.object, 1.0, 20);
    }
}
